import { Box, IconButton, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form-mui';
import { useModal } from '@refinedev/core';
import { MediaDialog } from '../medias/MediaDialog';
import Grid from '@mui/material/Grid2';
import { IMedia } from '@/interfaces';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { formatFileType } from '@/utils/formattingFunctions';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Memoized medias preview component for better performance
export const MediaPreview = ({
  media,
  index,
  onRemove,
  width = 120,
  height = 120,
  maxCount
}: {
  media: IMedia | string;
  index: number;
  onRemove?: () => void;
  width?: number;
  height?: number;
  maxCount?: number;
}) => {
  const formContext = useFormContext();


  const handleMakeCover = () => {
    if (typeof media === 'string' || !media.id) return;

    const currentMedias = formContext.getValues("medias");
    if (!currentMedias || currentMedias.length <= 1) return;

    // Find the current media in the array
    const mediaIndex = currentMedias.findIndex((item: IMedia) => item.id === media.id);
    if (mediaIndex === -1) return;

    // Create a new array with the selected media as the first item
    const [selectedMedia] = currentMedias.splice(mediaIndex, 1);
    currentMedias.unshift(selectedMedia);
    // Update the form value
    formContext.setValue("medias", currentMedias);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: width,
        height: height,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '&:hover': {
          '.make-cover': {
            opacity: 1,
          },
          '.media-preview': {
            opacity: index === 0 ? 1 : 0.5,
          },
          backgroundColor: index === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.7)',
          transition: 'all 0.2s',
        },
      }}
    >
      {onRemove && <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1,
          bgcolor: 'background.paper',
          boxShadow: 1,
          '&:hover': {
            bgcolor: 'background.paper',
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>}
      <Box
        className="media-preview"
        component={typeof media === 'string' ? 'img' : formatFileType(media.type)}
        src={typeof media === 'string' ? media : media.cdn_url}
        alt={typeof media === 'string' ? '' : media.filename}
        onMouseDown={(e: React.MouseEvent<HTMLImageElement>) => e.stopPropagation()}
        sx={{
          width: 'auto',
          height: '100%',
          objectFit: 'contain',
          opacity: 1,
          transition: 'opacity 0.2s',
        }}
        controls={false}
      />
      {index === 0 && maxCount !== 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            bgcolor: 'primary.main',
            color: 'grey.200',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          Cover
        </Box>
      )}
      {index !== 0 && maxCount !== 1 && (
        <Box
          className="make-cover"
          sx={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            bgcolor: 'primary.main',
            color: 'grey.200',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 500,
            cursor: 'pointer',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
          onClick={handleMakeCover}
        >
          Make Cover
        </Box>
      )}
    </Box>
  )
};

const SortableMediaItem = ({
  id,
  media,
  index,
  onRemove,
  maxCount,
}: {
  id: string;
  media: IMedia;
  index: number;
  onRemove: () => void;
  maxCount: number;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <Grid size={{ xs: "auto" }} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <MediaPreview
        media={media}
        index={index}
        onRemove={onRemove}
        maxCount={maxCount}
      />
    </Grid>
  );
};


// Add Image button component
const AddImageButton = ({ onClick, count, maxCount }: { onClick: () => void; count: number, maxCount: number }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 120,
      height: 120,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 1,
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        bgcolor: 'action.hover',
        borderColor: 'primary.main',
      },
    }}
  >
    <AddPhotoAlternateIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
    <Typography
      variant="caption"
      color="text.secondary"
      align="center"
    >
      Add Image
      <br />
      ({count}/{maxCount})
    </Typography>
  </Box>
);

export const AddMediaForm = ({ name, allowMultiple = true }: { name: string, allowMultiple?: boolean }) => {
  const { show, close, visible } = useModal();
  const { control, setValue } = useFormContext();

  const handleRemoveMedia = React.useCallback((mediaToRemove: IMedia, currentMedia: IMedia[]) => {
    const updatedMedia = currentMedia.filter(media => media.id !== mediaToRemove.id);
    setValue(name, updatedMedia);
  }, [setValue, name]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) return;

    const currentItems = control._formValues[name] as IMedia[];
    const oldIndex = currentItems.findIndex((item: IMedia) => item.id === active.id);
    const newIndex = currentItems.findIndex((item: IMedia) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(currentItems, oldIndex, newIndex);
    setValue(name, newOrder);
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value = [] }, fieldState: { error } }) => (
        <Box>
          <Box sx={{
            p: 2,
            border: '1px dashed',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}>
            <Grid container spacing={2}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={value.map((media: IMedia) => media.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Grid container spacing={2}>
                    {value.map((media: IMedia, index: number) => (
                      <SortableMediaItem
                        key={media.id}
                        id={media.id}
                        media={media}
                        index={index}
                        onRemove={() => handleRemoveMedia(media, value)}
                        maxCount={allowMultiple ? 20 : 1}
                      />
                    ))}
                    <Grid size={{ xs: "auto" }}>
                      <AddImageButton
                        onClick={show}
                        count={value.length}
                        maxCount={allowMultiple ? 20 : 1}
                      />
                    </Grid>
                  </Grid>
                </SortableContext>
              </DndContext>

            </Grid>
          </Box>

          {error && (
            <Typography color="error" variant="caption" sx={{ mt: 1, ml: 2 }}>
              {error.message}
            </Typography>
          )}

          <MediaDialog
            open={visible}
            onClose={close}
            name={name}
            allowMultiple={allowMultiple}
            onAdd={(medias) => {
              setValue(name, [...value, ...medias]);
            }}
          />
        </Box>
      )}
    />
  );
};
